import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';
import OptimizedImage from './OptimizedImage';
import { specialtiesSection, features, menuPage } from '@/data/content';
import { trackEvent } from '@/Services/analytics';
import { type ResolvedSlot } from '@/types/menu';
import { formatPrice } from '@/Services/format';

const toFallbackSlug = (value: string) =>
    String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

interface MenuItemProps {
    item: {
        id?: string | number;
        slug?: string;
        item_name: string;
        item_image?: string;
        image_path?: string;
        description?: string;
        price?: string | number;
        portion_quantity?: number;
        food_unit?: string;
        portion_label?: string;
        preparation_days?: number;
        daily_capacity?: number;
        weight_percentage?: number;
        isLoading?: boolean;
        canBeAddedToCart?: boolean;
    };
    loading?: boolean;
    selectedDate?: Date | null;
    isAvailable?: boolean;
    compact?: boolean;
    resolvedSlot?: ResolvedSlot | null;
    renderAddToBasket?: (foodId: number, price: number, isAvailable: boolean, maxQuantity?: number, isCompact?: boolean) => React.ReactNode;
}

const MenuItem: React.FC<MenuItemProps> = ({
    item,
    loading,
    selectedDate: _selectedDate,
    isAvailable = true,
    compact = false,
    resolvedSlot: _resolvedSlot,
    renderAddToBasket,
}) => {
    const detailSlug = item.slug || item.id || toFallbackSlug(item.item_name);
    const imageSrc = item.image_path || item.item_image;
    const availabilityResolved = item.isLoading !== true;
    const isOutOfStock = availabilityResolved && (item.daily_capacity ?? 0) === 0;

    return (
        <div
            className={cn(
                'flex flex-col h-full bg-app-surface rounded-[2rem] shadow-sm border border-app-border overflow-hidden group hover:shadow-2xl hover:shadow-app-primary/10 transition-all duration-500 hover:-translate-y-2',
                isOutOfStock && 'opacity-50',
                item.isLoading && 'opacity-60',
            )}
        >
            {/* Clickable area: image + text content */}
            <Link
                to={`/menu/${detailSlug}`}
                className="flex flex-col flex-grow focus:outline-none focus:ring-4 focus:ring-app-primary/50 rounded-3xl"
                onClick={() =>
                    trackEvent('menu_item_clicked', {
                        item_id: item.id,
                        item_name: item.item_name,
                        source: compact ? 'compact_grid' : 'full_grid',
                    })
                }
            >
                <div className="relative aspect-[4/3] bg-app-bg overflow-hidden">
                    <OptimizedImage
                        src={imageSrc || specialtiesSection.food_image_fallback}
                        alt={item.item_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                            (e.target as HTMLImageElement).src = specialtiesSection.food_image_fallback;
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {item.portion_label && (
                        <div className="absolute bottom-2 left-3 z-10 bg-white dark:bg-gray-800 text-app-primary text-[9px] sm:text-[10px] font-black px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg shadow-md border border-app-primary flex items-center gap-0.5 sm:gap-1">
                            <span className="text-xs sm:text-sm">🍽️</span>
                            <span>{item.portion_label}</span>
                        </div>
                    )}
                    {item.weight_percentage != null && (
                        <div className="absolute bottom-2 right-3 z-10 bg-white dark:bg-gray-800 text-app-primary text-[9px] sm:text-[10px] font-black px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg shadow-md border border-app-primary flex items-center gap-0.5 sm:gap-1">
                            <span className="text-xs sm:text-sm">🍳</span>
                            <span>{item.weight_percentage}%</span>
                        </div>
                    )}
                    {item.daily_capacity != null && (
                        <div className="absolute top-2 left-3 z-10 bg-white dark:bg-gray-800 text-app-primary text-[9px] sm:text-[10px] font-black px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg shadow-md border border-app-primary flex items-center gap-0.5 sm:gap-1">
                            <span className="text-xs sm:text-sm">📦</span>
                            <span>{item.daily_capacity}</span>
                        </div>
                    )}
                </div>

                <div className={cn('flex flex-col flex-grow', compact ? 'p-3' : 'p-4 sm:p-5')}>
                    {/* Compact mobile layout — hidden on sm+ */}
                    {compact && (
                        <div className="sm:hidden">
                            <h4 className="text-sm font-black text-app-text leading-snug group-hover:text-app-primary transition-colors mb-1">
                                {item.item_name}
                            </h4>
                            <div className="flex items-center gap-2">
                                {isAvailable === false && (
                                    <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">
                                        {menuPage.filter_indisponible}
                                    </span>
                                )}
                                <div className="ml-auto flex items-center gap-2">
                                    {item.price != null && Number.isFinite(Number(item.price)) && (
                                        <span className="text-sm font-black text-app-primary whitespace-nowrap">
                                            {formatPrice(Number(item.price))}
                                        </span>
                                    )}
                                    {renderAddToBasket?.(
                                        Number(item.id),
                                        Number(item.price) || 0,
                                        availabilityResolved &&
                                            item.canBeAddedToCart === true &&
                                            !isOutOfStock &&
                                            isAvailable !== false &&
                                            !loading,
                                        99,
                                        true
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Full layout: title, price, description — no footer */}
                    <div className={compact ? 'hidden sm:flex flex-col flex-grow' : 'flex flex-col flex-grow'}>
                        <div className="flex justify-between items-start mb-2 gap-3">
                            <h4 className="text-lg font-black text-app-text line-clamp-2 leading-tight group-hover:text-app-primary transition-colors">
                                {item.item_name}
                            </h4>
                            {item.price != null && Number.isFinite(Number(item.price)) && (
                                <span className="text-lg font-black text-app-primary whitespace-nowrap">
                                    {formatPrice(Number(item.price))}
                                </span>
                            )}
                        </div>

                        {isAvailable === false && (
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">
                                    {menuPage.filter_indisponible}
                                </span>
                            </div>
                        )}

                        {item.description && (
                            <p className="text-sm text-app-muted line-clamp-2 mb-2 flex-grow leading-snug font-medium">
                                {item.description}
                            </p>
                        )}
                    </div>
                </div>
            </Link>

            {features.order && renderAddToBasket?.(
                Number(item.id),
                Number(item.price) || 0,
                availabilityResolved && item.canBeAddedToCart === true && !isOutOfStock && isAvailable !== false,
                item.daily_capacity != null && item.daily_capacity > 0 ? item.daily_capacity : 0,
                false
            )}
        </div>
    );
};

export default MenuItem;
